import { Component, computed, input, output, signal, inject } from '@angular/core';
import { isUserType, IUser, USER_TYPES } from '@webapp/users/interfaces/users.interface';
import { UserUpdateFormComponent } from '@webapp/users';
import { CardComponent, ButtonComponent, BackButtonComponent } from '@webapp/ui';
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { UsersService } from '@webapp/users/services/users.service';

@Component({
  selector: 'app-user-data-step',
  standalone: true,
  imports: [UserUpdateFormComponent, CardComponent, ButtonComponent, BackButtonComponent, NgxMaskPipe],
  providers: [provideNgxMask()],
  templateUrl: './user-data-step.component.html',
  styleUrl: './user-data-step.component.scss'
})
export class UserDataStepComponent {
  readonly USER_TYPES = USER_TYPES;
  readonly isUserType = isUserType;
  // Injected services
  private readonly usersService = inject(UsersService);

  // Inputs
  readonly user = input.required<IUser>();
  readonly loading = input<boolean>(false);

  // Outputs
  readonly userUpdated = output<IUser>();
  readonly continueWithoutUpdate = output<void>();

  // Signals
  readonly isEditing = signal(false);

  // Computed
  readonly hasCompleteData = computed(() => {
    const userData = this.user();
    return this.usersService.validateUserData(userData);
  });

  readonly shouldShowInfo = computed(() => {
    return this.hasCompleteData() && !this.isEditing();
  });

  readonly shouldShowForm = computed(() => {
    return !this.hasCompleteData() || this.isEditing();
  });

  protected startEditing(): void {
    this.isEditing.set(true);
  }

  protected cancelEditing(): void {
    this.isEditing.set(false);
  }

  protected onUserUpdated(updatedUser: IUser): void {
    this.userUpdated.emit(updatedUser);
    this.isEditing.set(false);
  }

  protected onContinueWithoutUpdate(): void {
    this.continueWithoutUpdate.emit();
  }

  protected get displayName(): string {
    const userData = this.user();
    if (userData.type === USER_TYPES.PERSON.toUpperCase()) {
      return `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
    }
    return userData.companyName || '';
  }

  protected get identificationLabel(): string {
    return this.user().type === USER_TYPES.PERSON.toUpperCase() ? 'DNI' : 'CUIT';
  }

  protected get identificationMask(): string {
    return this.user().type === USER_TYPES.PERSON.toUpperCase() ? '00.000.000' : '00-00000000-0';
  }

  protected formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'No especificado';

    // birthDate is a calendar date (YYYY-MM-DD): parsing it with new Date() would read it as UTC midnight
    const [year, month, day] = dateString.slice(0, 10).split('-');
    if (!year || !month || !day) return 'No especificado';

    return `${day}-${month}-${year}`;
  }
}